import React from 'react';
import {
    MessageSquare, Clock, CheckCircle, XCircle, AlertCircle,
    Calendar, User, Eye, Trash2, Star
} from 'lucide-react';
import { ReviewRequest } from '../../../services/collaboration.service';

interface ReviewCardProps {
    review: ReviewRequest;
    onView: (review: ReviewRequest) => void;
    onDelete: (review: ReviewRequest) => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onView, onDelete }) => {
    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'pending':
                return {
                    icon: Clock,
                    label: 'Pending',
                    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
                    text: 'text-yellow-700 dark:text-yellow-300',
                    iconColor: 'text-yellow-500',
                };
            case 'in_review':
                return {
                    icon: Eye,
                    label: 'In Review',
                    bg: 'bg-blue-100 dark:bg-blue-900/30',
                    text: 'text-blue-700 dark:text-blue-300',
                    iconColor: 'text-blue-500',
                };
            case 'changes_requested':
                return {
                    icon: AlertCircle,
                    label: 'Changes Requested',
                    bg: 'bg-orange-100 dark:bg-orange-900/30',
                    text: 'text-orange-700 dark:text-orange-300',
                    iconColor: 'text-orange-500',
                };
            case 'approved':
                return {
                    icon: CheckCircle,
                    label: 'Approved',
                    bg: 'bg-green-100 dark:bg-green-900/30',
                    text: 'text-green-700 dark:text-green-300',
                    iconColor: 'text-green-500',
                };
            case 'rejected':
                return {
                    icon: XCircle,
                    label: 'Rejected',
                    bg: 'bg-red-100 dark:bg-red-900/30',
                    text: 'text-red-700 dark:text-red-300',
                    iconColor: 'text-red-500',
                };
            default:
                return {
                    icon: Clock,
                    label: status,
                    bg: 'bg-gray-100 dark:bg-gray-700',
                    text: 'text-gray-700 dark:text-gray-300',
                    iconColor: 'text-gray-500',
                };
        }
    };

    const getPriorityConfig = (priority: string) => {
        switch (priority) {
            case 'urgent':
                return { color: 'text-red-500', fill: 'fill-red-500' };
            case 'high':
                return { color: 'text-orange-500', fill: 'fill-orange-500' };
            case 'medium':
                return { color: 'text-yellow-500', fill: 'fill-yellow-500' };
            default:
                return { color: 'text-gray-400 dark:text-gray-500', fill: '' };
        }
    };

    const statusConfig = getStatusConfig(review.status);
    const priorityConfig = getPriorityConfig(review.priority);
    const StatusIcon = statusConfig.icon;

    const formatDate = (dateString?: string) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <div
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 cursor-pointer"
            onClick={() => onView(review)}
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Star className={`w-4 h-4 ${priorityConfig.color} ${priorityConfig.fill}`} />
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                            {review.title}
                        </h3>
                    </div>
                    {review.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {review.description}
                        </p>
                    )}
                </div>
            </div>

            {/* Status Badge */}
            <div className="flex items-center gap-2 mb-4">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                    <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.iconColor}`} />
                    {statusConfig.label}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {review.priority} priority
                </span>
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                {review.assignedTo && (
                    <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>Assigned</span>
                    </div>
                )}
                {review.dueDate && (
                    <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Due {formatDate(review.dueDate)}</span>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    <span>{review.reviewers.length} reviewers</span>
                </div>
            </div>

            {/* Decision (if approved/rejected) */}
            {review.decision && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                            {review.decision.action === 'approved' ? '✅ Approved' :
                                review.decision.action === 'rejected' ? '❌ Rejected' :
                                    '⚠️ Changes Requested'}
                        </span>
                        {review.decision.decidedAt && (
                            <span className="text-gray-500 dark:text-gray-400">
                                • {formatDate(review.decision.decidedAt)}
                            </span>
                        )}
                    </div>
                    {review.decision.comment && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {review.decision.comment}
                        </p>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onView(review);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                    <Eye className="w-4 h-4" />
                    View Details
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(review);
                    }}
                    className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default ReviewCard;
